import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  LinearProgress,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import BulkActionConfirmStep from "../search/BulkActionConfirmStep";
import {
  buildHygieneApplyChangeLines,
  buildDuplicateClusters,
  clusterMatchesDuplicateConfidenceFilter,
  duplicateConfidenceChipColor,
  formatDuplicateConfidenceLabel,
  formatHygieneCurrentValue,
  formatHygieneSuggestedValue,
  formatMatchByLabels,
  getIssueMaxDuplicateConfidence,
  isActionableAutoFix,
  issueMatchesDuplicateConfidenceFilter,
} from "./clientHygieneDisplay";
import HygieneDuplicateClusters from "./HygieneDuplicateClusters";
import { HYGIENE_RULES, useClientHygiene } from "../../hooks/useClientHygiene";
import {
  clearActiveHygieneJob,
  loadActiveHygieneJob,
  saveActiveHygieneJob,
} from "../../utils/clientHygieneJobStorage";
import {
  estimateJobEtaSeconds,
  formatEtaSeconds,
  formatExpiresIn,
  scanTokenExpiresAt,
} from "../../utils/clientHygieneEta";
import {
  notifyHygieneJobComplete,
  requestHygieneNotificationPermission,
} from "../../utils/clientHygieneNotify";

const STEPS = { config: 0, scanning: 1, review: 2, done: 3 };
const ASYNC_SCAN_THRESHOLD = 200;
const ASYNC_SCAN_MAX = 10000;
const SYNC_SCAN_MAX = 500;

function jobStatusI18nKey(status) {
  const map = {
    queued: "statusQueued",
    running: "statusRunning",
    cancel_requested: "statusCancelling",
    cancelled: "statusCancelled",
    failed: "statusFailed",
    completed: "statusCompleted",
  };
  return map[status] || "statusRunning";
}

function ruleLabel(ruleId) {
  return HYGIENE_RULES.find((r) => r.id === ruleId)?.label || ruleId;
}

function openClientInNewTab(clientId) {
  const id = Number(clientId);
  if (!Number.isFinite(id) || id <= 0) return;
  window.open(`/clients/${id}`, "_blank", "noopener,noreferrer");
}

function stopRowNavigation(event) {
  event.stopPropagation();
}

export default function ClientHygieneDialog({
  open,
  onClose,
  selectedIds = [],
  appliedFilters = {},
  hasActiveFilters = false,
  filterTotal = 0,
  onApplied,
  onMergeDuplicates,
}) {
  const { t } = useTranslation("settings");
  const {
    loading,
    error,
    scanSync,
    startJob,
    pollJob,
    cancelJob,
    fetchJob,
    applyFixes,
    stopPolling,
    clearError,
  } = useClientHygiene();

  const [step, setStep] = useState(STEPS.config);
  const [scope, setScope] = useState("selection");
  const [mode, setMode] = useState("review");
  const [rules, setRules] = useState(() => HYGIENE_RULES.map((r) => r.id));
  const [applyConfirmOpen, setApplyConfirmOpen] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [jobMeta, setJobMeta] = useState(null);
  const [selectedIssues, setSelectedIssues] = useState(new Set());
  const [reviewFilter, setReviewFilter] = useState("auto");
  const [duplicateConfidenceFilter, setDuplicateConfidenceFilter] = useState("all");
  const [applyResult, setApplyResult] = useState(null);
  const [resumeOffer, setResumeOffer] = useState(null);
  const [resumeChecking, setResumeChecking] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [scanLogOpen, setScanLogOpen] = useState(true);
  const [localError, setLocalError] = useState(null);
  const [scanStartedAt, setScanStartedAt] = useState(null);
  const openRef = useRef(open);

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  const impactCount = useMemo(() => {
    if (scope === "filter") return filterTotal || 0;
    return selectedIds.length;
  }, [scope, filterTotal, selectedIds.length]);

  const issues = scanResult?.issues || jobMeta?.issues || [];
  const scanToken = scanResult?.scanToken || jobMeta?.scanToken;
  const summary = scanResult?.summary || jobMeta?.summary;

  const filteredIssues = useMemo(() => {
    let list = issues;
    if (reviewFilter === "auto") list = issues.filter(isActionableAutoFix);
    else if (reviewFilter === "duplicates") {
      list = issues.filter((i) => i.rule === "duplicate_candidate");
      list = list.filter((issue) => issueMatchesDuplicateConfidenceFilter(issue, duplicateConfidenceFilter));
    } else if (reviewFilter === "names") {
      list = issues.filter((i) => i.rule === "name_invalid" || i.rule === "name_missing");
    }
    return list;
  }, [issues, reviewFilter, duplicateConfidenceFilter]);

  const duplicateClusters = useMemo(() => {
    return buildDuplicateClusters(issues).filter((cluster) =>
      clusterMatchesDuplicateConfidenceFilter(cluster, duplicateConfidenceFilter)
    );
  }, [issues, duplicateConfidenceFilter]);

  const isDuplicatesView = reviewFilter === "duplicates";

  const scanPlan = useMemo(() => {
    const count = impactCount;
    const useAsync =
      (scope === "filter" && count > ASYNC_SCAN_THRESHOLD) ||
      (scope === "selection" && count > ASYNC_SCAN_THRESHOLD);
    return {
      useAsync,
      dupRule: rules.includes("duplicate_candidate"),
      rulesCount: rules.length,
      mode,
      count,
    };
  }, [impactCount, scope, rules, mode]);

  const jobEtaSeconds = useMemo(
    () => estimateJobEtaSeconds(jobMeta, scanStartedAt),
    [jobMeta, scanStartedAt]
  );

  const tokenExpiresAt = useMemo(
    () => scanTokenExpiresAt(jobMeta?.finishedAt || scanResult?.finishedAt),
    [jobMeta?.finishedAt, scanResult?.finishedAt]
  );
  const tokenExpiresIn = useMemo(() => formatExpiresIn(tokenExpiresAt), [tokenExpiresAt]);

  const displayError = localError || error;

  const resetDialogState = ({ preserveResume = false } = {}) => {
    setStep(STEPS.config);
    setScope(selectedIds.length ? "selection" : hasActiveFilters ? "filter" : "selection");
    setMode("review");
    setRules(HYGIENE_RULES.map((r) => r.id));
    setApplyConfirmOpen(false);
    setScanResult(null);
    setJobMeta(null);
    setSelectedIssues(new Set());
    setReviewFilter("auto");
    setDuplicateConfidenceFilter("all");
    setApplyResult(null);
    if (!preserveResume) setResumeOffer(null);
    clearError();
    setLocalError(null);
    setScanStartedAt(null);
    stopPolling();
  };

  const handleApply = async (token, issueIdSet, allAuto = false) => {
    if (!token) return;
    try {
      const result = await applyFixes({
        scanToken: token,
        issueIds: allAuto ? undefined : [...issueIdSet],
        applyAllAutoFixable: allAuto,
      });
      setApplyResult(result);
      setStep(STEPS.done);
      onApplied?.();
    } catch {
      /* error in hook */
    }
  };

  const applyJobToReview = (job) => {
    setJobMeta(job);
    setScanResult({
      issues: job.issues || [],
      summary: job.summary,
      scanToken: job.scanToken,
      scanned: job.scanned,
    });
    const autoIds = new Set((job.issues || []).filter(isActionableAutoFix).map((i) => i.issueId));
    setSelectedIssues(autoIds);
    setReviewFilter("auto");
    setStep(STEPS.review);
    clearActiveHygieneJob();
    setResumeOffer(null);
    return autoIds;
  };

  const attachJobPolling = (jobId, meta = {}) => {
    const startedAt = meta.startedAt || new Date().toISOString();
    setScanStartedAt(startedAt);
    saveActiveHygieneJob({
      jobId,
      startedAt,
      scope: meta.scope ?? scope,
      impactCount: meta.impactCount ?? impactCount,
      mode: meta.mode ?? mode,
      rulesCount: meta.rulesCount ?? rules.length,
      phase: "running",
    });
    setResumeOffer(null);
    setStep(STEPS.scanning);
    setJobMeta({ jobId, status: "queued" });

    pollJob(jobId, {
      onUpdate: (job) => setJobMeta((prev) => ({ ...prev, ...job })),
      onComplete: (job) => {
        if (!openRef.current) {
          saveActiveHygieneJob({
            jobId: job.jobId || jobId,
            phase: "completed",
            startedAt,
            scope: meta.scope ?? scope,
            impactCount: meta.impactCount ?? impactCount,
          });
          notifyHygieneJobComplete({
            scanned: job.scanned,
            issuesFound: job.summary?.total ?? job.issuesFound ?? 0,
          });
          return;
        }
        const autoIds = applyJobToReview(job);
        if (job.mode === "auto") handleApply(job.scanToken, autoIds, true);
      },
      onError: (jobOrErr) => {
        clearActiveHygieneJob();
        const failedJob =
          typeof jobOrErr === "object" && jobOrErr !== null && !(jobOrErr instanceof Error)
            ? jobOrErr
            : { status: "failed", errorMessage: jobOrErr?.message };
        setJobMeta(failedJob);
        setStep(STEPS.config);
      },
    });
  };

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    setResumeChecking(true);

    (async () => {
      const stored = loadActiveHygieneJob();
      if (stored?.jobId) {
        try {
          const job = await fetchJob(stored.jobId);
          if (cancelled) return;

          if (job && ["queued", "running", "cancel_requested"].includes(job.status)) {
            resetDialogState({ preserveResume: true });
            setResumeOffer({ jobId: stored.jobId, job, phase: "running" });
            setResumeChecking(false);
            return;
          }

          if (job?.status === "completed") {
            resetDialogState({ preserveResume: true });
            setResumeOffer({ jobId: stored.jobId, job, phase: "completed" });
            setResumeChecking(false);
            return;
          }

          clearActiveHygieneJob();
        } catch {
          clearActiveHygieneJob();
        }
      }

      if (!cancelled) {
        resetDialogState();
        setResumeChecking(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, selectedIds.length, hasActiveFilters, stopPolling, clearError, fetchJob]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const toggleRule = (id) => {
    setRules((prev) => {
      const set = new Set(prev);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return [...set];
    });
  };

  const handleDismissResume = () => {
    clearActiveHygieneJob();
    setResumeOffer(null);
  };

  const handleContinueResume = () => {
    if (!resumeOffer?.jobId) return;
    attachJobPolling(resumeOffer.jobId);
  };

  const handleViewResumeResults = () => {
    if (!resumeOffer?.job) return;
    const autoIds = applyJobToReview(resumeOffer.job);
    if (resumeOffer.job.mode === "auto") handleApply(resumeOffer.job.scanToken, autoIds, true);
  };

  const runScan = async () => {
    const selectionCount = selectedIds.length;
    const count = scope === "filter" ? filterTotal || 0 : selectionCount;
    const useAsyncJob =
      (scope === "filter" && filterTotal > ASYNC_SCAN_THRESHOLD) ||
      (scope === "selection" && selectionCount > ASYNC_SCAN_THRESHOLD);

    setLocalError(null);
    clearError();

    if (count > ASYNC_SCAN_MAX) {
      setLocalError(
        t("clientHygiene.errorTooManyRecords", { max: ASYNC_SCAN_MAX.toLocaleString() })
      );
      return;
    }

    if (!useAsyncJob && count > SYNC_SCAN_MAX) {
      setLocalError(
        t("clientHygiene.errorSyncLimit", { max: SYNC_SCAN_MAX.toLocaleString() })
      );
      return;
    }

    if (!rules.length) {
      setLocalError(t("clientHygiene.errorNoRules"));
      return;
    }

    setStep(STEPS.scanning);
    setScanResult(null);
    setJobMeta(null);

    try {
      if (useAsyncJob) {
        requestHygieneNotificationPermission();
        const jobStart = await startJob({
          filters: scope === "filter" ? appliedFilters : undefined,
          ids: scope === "selection" ? selectedIds : undefined,
          rules,
          mode,
        });
        const jobId = jobStart?.jobId;
        if (!jobId) throw new Error("Job não iniciado.");

        attachJobPolling(jobId, {
          scope,
          impactCount: count,
          mode,
          rulesCount: rules.length,
        });
        return;
      }

      const data = await scanSync({
        ids: scope === "selection" ? selectedIds : undefined,
        filters: scope === "filter" ? appliedFilters : undefined,
        rules,
      });
      setScanResult({ ...data, finishedAt: new Date().toISOString() });
      const autoIds = new Set(
        (data.issues || []).filter(isActionableAutoFix).map((i) => i.issueId)
      );
      setSelectedIssues(autoIds);
      if (mode === "auto") {
        await handleApply(data.scanToken, autoIds, true);
      } else {
        setReviewFilter("auto");
        setStep(STEPS.review);
      }
    } catch (err) {
      if (err?.code === "HYGIENE_JOB_ACTIVE" && err?.activeJobId) {
        setResumeOffer({
          jobId: err.activeJobId,
          job: err.activeJob,
          phase: "running",
        });
        setLocalError(t("clientHygiene.errorActiveJob"));
      }
      setStep(STEPS.config);
    }
  };

  const handleConfirmCancelJob = async () => {
    setCancelConfirmOpen(false);
    if (!jobMeta?.jobId) return;
    await cancelJob(jobMeta.jobId);
    clearActiveHygieneJob();
    stopPolling();
    setJobMeta({ status: "cancelled" });
    setStep(STEPS.config);
  };

  const toggleIssue = (issueId) => {
    setSelectedIssues((prev) => {
      const next = new Set(prev);
      if (next.has(issueId)) next.delete(issueId);
      else next.add(issueId);
      return next;
    });
  };

  const handleStartScan = () => {
    if (mode === "auto") {
      setApplyConfirmOpen(true);
      return;
    }
    runScan();
  };

  const handleRequestApply = () => {
    setApplyConfirmOpen(true);
  };

  const handleConfirmApply = () => {
    setApplyConfirmOpen(false);
    if (step === STEPS.config && mode === "auto") {
      runScan();
      return;
    }
    if (step === STEPS.review && scanToken) {
      handleApply(scanToken, selectedIssues, false);
    }
  };

  const applyConfirmCount =
    step === STEPS.config && mode === "auto"
      ? impactCount
      : selectedIssues.size;

  const applyConfirmIsAutoStart = step === STEPS.config && mode === "auto";

  const applyChangeLines = useMemo(
    () => buildHygieneApplyChangeLines(issues, selectedIssues, t),
    [issues, selectedIssues, t]
  );

  const canStart =
    (scope === "selection" && selectedIds.length > 0) ||
    (scope === "filter" && hasActiveFilters);

  return (
    <>
      <Dialog open={open && !applyConfirmOpen} onClose={onClose} fullWidth maxWidth="lg">
        <DialogTitle>{t("clientHygiene.title")}</DialogTitle>
        <DialogContent dividers>
          {displayError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {displayError}
            </Alert>
          ) : null}

          {jobMeta?.status === "failed" && step === STEPS.config ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {jobMeta.errorMessage || t("clientHygiene.statusFailed")}
            </Alert>
          ) : null}

          {step === STEPS.config && (
            <Stack spacing={2}>
              {resumeChecking ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    {t("clientHygiene.resumeChecking")}
                  </Typography>
                </Stack>
              ) : null}

              {resumeOffer?.phase === "running" ? (
                <Alert severity="info">
                  <Typography variant="body2">
                    {t("clientHygiene.resumeRunning", {
                      processed: resumeOffer.job?.processed || resumeOffer.job?.scanned || 0,
                      total: resumeOffer.job?.total || "?",
                      progress: resumeOffer.job?.progress ?? 0,
                    })}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                    <Button size="small" variant="contained" onClick={handleContinueResume}>
                      {t("clientHygiene.resumeContinue")}
                    </Button>
                    <Button size="small" onClick={handleDismissResume}>
                      {t("clientHygiene.resumeDismiss")}
                    </Button>
                  </Stack>
                </Alert>
              ) : null}

              {resumeOffer?.phase === "completed" ? (
                <Alert severity="success">
                  <Typography variant="body2">
                    {t("clientHygiene.resumeCompleted", {
                      total: resumeOffer.job?.summary?.total ?? resumeOffer.job?.issues?.length ?? 0,
                      scanned: resumeOffer.job?.scanned || 0,
                    })}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                    <Button size="small" variant="contained" onClick={handleViewResumeResults}>
                      {t("clientHygiene.resumeViewResults")}
                    </Button>
                    <Button size="small" onClick={handleDismissResume}>
                      {t("clientHygiene.resumeDismiss")}
                    </Button>
                  </Stack>
                </Alert>
              ) : null}

              {mode === "auto" ? (
                <Alert severity="warning">{t("clientHygiene.irreversibleWarning")}</Alert>
              ) : (
                <Alert severity="info">{t("clientHygiene.scanOnlyHint")}</Alert>
              )}
              {impactCount > ASYNC_SCAN_THRESHOLD ? (
                <Alert severity="info">{t("clientHygiene.scanAsyncHint")}</Alert>
              ) : null}

              {canStart && rules.length ? (
                <Alert severity="info" icon={false}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t("clientHygiene.planTitle")}
                  </Typography>
                  <Typography variant="body2" component="div">
                    <ul style={{ margin: "4px 0 0", paddingLeft: 20 }}>
                      <li>
                        {t("clientHygiene.planScope", {
                          count: scanPlan.count.toLocaleString(),
                          scope:
                            scope === "filter"
                              ? t("clientHygiene.planScopeFilter")
                              : t("clientHygiene.planScopeSelection"),
                        })}
                      </li>
                      <li>
                        {scanPlan.useAsync
                          ? t("clientHygiene.planAsync")
                          : t("clientHygiene.planSync")}
                      </li>
                      <li>{t("clientHygiene.planRules", { count: scanPlan.rulesCount })}</li>
                      <li>
                        {scanPlan.mode === "auto"
                          ? t("clientHygiene.planModeAuto")
                          : t("clientHygiene.planModeReview")}
                      </li>
                      <li>{t("clientHygiene.planReadOnly")}</li>
                      {scanPlan.dupRule ? (
                        <li>{t("clientHygiene.planDupLoad")}</li>
                      ) : null}
                    </ul>
                  </Typography>
                </Alert>
              ) : null}

              <Typography variant="subtitle2">{t("clientHygiene.scopeLabel")}</Typography>
              <RadioGroup value={scope} onChange={(e) => setScope(e.target.value)}>
                <FormControlLabel
                  value="selection"
                  control={<Radio />}
                  disabled={!selectedIds.length}
                  label={t("clientHygiene.scopeSelection", { count: selectedIds.length })}
                />
                <FormControlLabel
                  value="filter"
                  control={<Radio />}
                  disabled={!hasActiveFilters}
                  label={t("clientHygiene.scopeFilter", { count: filterTotal || "?" })}
                />
              </RadioGroup>

              <Typography variant="subtitle2">{t("clientHygiene.modeLabel")}</Typography>
              <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
                <FormControlLabel
                  value="review"
                  control={<Radio />}
                  label={t("clientHygiene.modeReview")}
                />
                <FormControlLabel
                  value="auto"
                  control={<Radio />}
                  label={t("clientHygiene.modeAuto")}
                />
              </RadioGroup>

              <Typography variant="subtitle2">{t("clientHygiene.rulesLabel")}</Typography>
              <FormGroup>
                {HYGIENE_RULES.map((r) => (
                  <FormControlLabel
                    key={r.id}
                    control={
                      <Checkbox
                        checked={rules.includes(r.id)}
                        onChange={() => toggleRule(r.id)}
                      />
                    }
                    label={r.label}
                  />
                ))}
              </FormGroup>
            </Stack>
          )}

          {step === STEPS.scanning && (
            <Stack spacing={2} py={2}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <CircularProgress size={22} />
                <Typography fontWeight={600}>{t("clientHygiene.scanning")}</Typography>
                {jobMeta?.status ? (
                  <Chip
                    size="small"
                    label={t(`clientHygiene.${jobStatusI18nKey(jobMeta.status)}`)}
                    color={
                      jobMeta.status === "failed"
                        ? "error"
                        : jobMeta.status === "queued"
                        ? "default"
                        : "primary"
                    }
                    variant="outlined"
                  />
                ) : null}
              </Stack>

              <Alert severity="info" sx={{ py: 0.75 }}>
                {t("clientHygiene.scanBackgroundHint")}
              </Alert>

              {jobMeta?.progress != null ? (
                <>
                  <LinearProgress
                    variant="determinate"
                    value={jobMeta.progress}
                    sx={{ width: "100%" }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {t("clientHygiene.scanProgress", {
                      processed: (jobMeta.processed || 0).toLocaleString(),
                      total: jobMeta.total != null ? Number(jobMeta.total).toLocaleString() : "?",
                      progress: jobMeta.progress,
                    })}
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t("clientHygiene.scanQueued")}
                </Typography>
              )}

              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Typography variant="body2">
                  {t("clientHygiene.scanIssuesFound", {
                    count: (jobMeta?.issuesFound ?? 0).toLocaleString(),
                  })}
                </Typography>
                {jobEtaSeconds != null ? (
                  <Typography variant="body2" color="text.secondary">
                    {t("clientHygiene.scanEta", { eta: formatEtaSeconds(jobEtaSeconds) })}
                  </Typography>
                ) : null}
              </Stack>

              {Array.isArray(jobMeta?.log) && jobMeta.log.length ? (
                <Box>
                  <Button size="small" onClick={() => setScanLogOpen((v) => !v)}>
                    {scanLogOpen
                      ? t("clientHygiene.scanLogHide")
                      : t("clientHygiene.scanLogShow")}
                  </Button>
                  <Collapse in={scanLogOpen}>
                    <Box
                      sx={{
                        mt: 1,
                        p: 1.5,
                        bgcolor: "action.hover",
                        borderRadius: 1,
                        maxHeight: 160,
                        overflow: "auto",
                        fontFamily: "monospace",
                        fontSize: 12,
                      }}
                    >
                      {jobMeta.log.slice(-12).map((line, idx) => (
                        <Typography key={`${idx}-${line}`} variant="caption" display="block">
                          {line}
                        </Typography>
                      ))}
                    </Box>
                  </Collapse>
                </Box>
              ) : null}

              {jobMeta?.jobId ? (
                <Button size="small" color="warning" onClick={() => setCancelConfirmOpen(true)}>
                  {t("clientHygiene.cancelJob")}
                </Button>
              ) : null}
            </Stack>
          )}

          {step === STEPS.review && (
            <Stack spacing={2}>
              <Alert severity="info">
                {t("clientHygiene.scanSummary", {
                  total: summary?.total ?? issues.length,
                  auto: summary?.autoFixable ?? 0,
                  dup: summary?.duplicates ?? 0,
                })}
              </Alert>
              {scanToken && tokenExpiresIn ? (
                <Alert
                  severity={tokenExpiresAt && tokenExpiresAt.getTime() - Date.now() < 15 * 60 * 1000 ? "warning" : "info"}
                >
                  {t("clientHygiene.scanTokenExpiry", { expiresIn: tokenExpiresIn })}
                </Alert>
              ) : null}
              <Typography variant="body2" color="text.secondary">
                {isDuplicatesView
                  ? t("clientHygiene.reviewDuplicatesHint")
                  : t("clientHygiene.reviewApplyHint")}
              </Typography>

              <Stack direction="row" spacing={1} flexWrap="wrap">
                {[
                  ["all", t("clientHygiene.filterAll")],
                  ["auto", t("clientHygiene.filterAuto")],
                  ["duplicates", t("clientHygiene.filterDuplicates")],
                  ["names", t("clientHygiene.filterNames")],
                ].map(([key, label]) => (
                  <Button
                    key={key}
                    size="small"
                    variant={reviewFilter === key ? "contained" : "outlined"}
                    onClick={() => setReviewFilter(key)}
                  >
                    {label}
                  </Button>
                ))}
              </Stack>

              {reviewFilter === "duplicates" ? (
                <Stack spacing={0.5}>
                  <Typography variant="caption" color="text.secondary">
                    {t("clientHygiene.duplicateConfidenceHint")}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {[
                      ["all", t("clientHygiene.filterDupAll")],
                      ["high", t("clientHygiene.filterDupHigh")],
                      ["medium_plus", t("clientHygiene.filterDupMediumPlus")],
                    ].map(([key, label]) => (
                      <Button
                        key={key}
                        size="small"
                        variant={duplicateConfidenceFilter === key ? "contained" : "outlined"}
                        color={key === "high" ? "success" : key === "medium_plus" ? "warning" : "inherit"}
                        onClick={() => setDuplicateConfidenceFilter(key)}
                      >
                        {label}
                      </Button>
                    ))}
                  </Stack>
                </Stack>
              ) : null}

              {isDuplicatesView ? (
                <Box sx={{ maxHeight: 420, overflow: "auto" }}>
                  <HygieneDuplicateClusters
                    clusters={duplicateClusters}
                    t={t}
                    onOpenClient={openClientInNewTab}
                    onMergeCluster={(primaryId, secondaries, allMembers) => {
                      onMergeDuplicates?.(primaryId, secondaries, { members: allMembers });
                    }}
                  />
                </Box>
              ) : (
              <Box sx={{ maxHeight: 360, overflow: "auto" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell>ID</TableCell>
                      <TableCell>{t("clientHygiene.colField")}</TableCell>
                      <TableCell>{t("clientHygiene.colCurrent")}</TableCell>
                      <TableCell>{t("clientHygiene.colChange")}</TableCell>
                      <TableCell>{t("clientHygiene.colRule")}</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredIssues.map((issue) => (
                      <TableRow
                        key={issue.issueId}
                        hover
                        onClick={() => openClientInNewTab(issue.clientId)}
                        sx={{ cursor: "pointer" }}
                        title={t("clientHygiene.openClientHint")}
                      >
                        <TableCell padding="checkbox" onClick={stopRowNavigation}>
                          {isActionableAutoFix(issue) ? (
                            <Checkbox
                              size="small"
                              checked={selectedIssues.has(issue.issueId)}
                              onChange={() => toggleIssue(issue.issueId)}
                              onClick={stopRowNavigation}
                            />
                          ) : null}
                        </TableCell>
                        <TableCell sx={{ color: "primary.main", fontWeight: 600 }}>
                          {issue.clientId}
                        </TableCell>
                        <TableCell>{issue.field}</TableCell>
                        <TableCell>
                          {formatHygieneCurrentValue(issue, t) ?? (
                            <Typography component="span" variant="body2" color="text.secondary" fontStyle="italic">
                              {t("clientHygiene.valueEmpty")}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {issue.rule === "duplicate_candidate" ? (
                            <Typography variant="body2" color="text.secondary">
                              {t("clientHygiene.useDuplicatesTab")}
                            </Typography>
                          ) : isActionableAutoFix(issue) ? (
                            <Typography variant="body2">
                              <Box component="span" color="text.secondary">
                                {formatHygieneCurrentValue(issue, t) ?? t("clientHygiene.valueEmpty")}
                              </Box>
                              {" → "}
                              <Box component="span" fontWeight={600} color="success.dark">
                                {formatHygieneSuggestedValue(issue, t)}
                              </Box>
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary" fontStyle="italic">
                              {formatHygieneSuggestedValue(issue, t)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                            <Typography variant="body2">{ruleLabel(issue.rule)}</Typography>
                            {issue.rule === "duplicate_candidate" ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                color={duplicateConfidenceChipColor(getIssueMaxDuplicateConfidence(issue).level)}
                                label={formatDuplicateConfidenceLabel(getIssueMaxDuplicateConfidence(issue).level, t)}
                              />
                            ) : isActionableAutoFix(issue) ? (
                              <Chip label={t("clientHygiene.badgeAuto")} size="small" color="primary" variant="outlined" />
                            ) : (
                              <Chip label={t("clientHygiene.badgeManual")} size="small" variant="outlined" />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell onClick={stopRowNavigation}>
                          {null}
                        </TableCell>
                      </TableRow>
                    ))}
                    {!filteredIssues.length ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          {t("clientHygiene.noIssues")}
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </Box>
              )}
            </Stack>
          )}

          {step === STEPS.done && (
            <Alert severity="success">
              {t("clientHygiene.applyDone", {
                applied: applyResult?.applied ?? 0,
                failed: applyResult?.failed ?? 0,
              })}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t("shared.cancel")}</Button>
          {step === STEPS.config && (
            <Button
              variant="contained"
              disabled={!canStart || !rules.length || loading}
              onClick={handleStartScan}
            >
              {t("clientHygiene.startScan")}
            </Button>
          )}
          {step === STEPS.review && !isDuplicatesView && (
            <Button
              variant="contained"
              disabled={loading || !scanToken || selectedIssues.size === 0}
              onClick={handleRequestApply}
            >
              {t("clientHygiene.applySelected", { count: selectedIssues.size })}
            </Button>
          )}
          {step === STEPS.review && isDuplicatesView && (
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1, alignSelf: "center" }}>
              {t("clientHygiene.duplicatesFooterHint")}
            </Typography>
          )}
          {step === STEPS.done && (
            <Button variant="contained" onClick={onClose}>
              {t("clientHygiene.close")}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={cancelConfirmOpen} onClose={() => setCancelConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("clientHygiene.cancelConfirmTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{t("clientHygiene.cancelConfirmBody")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelConfirmOpen(false)}>{t("shared.cancel")}</Button>
          <Button color="warning" variant="contained" onClick={handleConfirmCancelJob}>
            {t("clientHygiene.cancelJob")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={applyConfirmOpen} onClose={() => setApplyConfirmOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {applyConfirmIsAutoStart
            ? t("clientHygiene.confirmAutoTitle")
            : t("clientHygiene.confirmApplyTitle")}
        </DialogTitle>
        <BulkActionConfirmStep
          ids={applyConfirmIsAutoStart && scope === "selection" ? selectedIds : []}
          impactCount={applyConfirmCount}
          entitySingular={
            applyConfirmIsAutoStart
              ? t("clientHygiene.entitySingular")
              : t("clientHygiene.correctionSingular")
          }
          entityPlural={
            applyConfirmIsAutoStart
              ? t("clientHygiene.entityPlural")
              : t("clientHygiene.correctionPlural")
          }
          actionTitle={
            applyConfirmIsAutoStart
              ? t("clientHygiene.confirmAutoTitle")
              : t("clientHygiene.confirmApplyTitle")
          }
          actionDescription={
            applyConfirmIsAutoStart
              ? t("clientHygiene.confirmAutoDescription")
              : t("clientHygiene.confirmApplyDescription")
          }
          impactVerb={
            applyConfirmIsAutoStart
              ? t("clientHygiene.impactVerbAuto")
              : t("clientHygiene.impactVerbApply")
          }
          impactedIdsLabel={
            applyConfirmIsAutoStart && scope === "selection"
              ? t("clientHygiene.confirmIdsLabel", { count: applyConfirmCount })
              : undefined
          }
          impactedIdsFallback={
            applyConfirmIsAutoStart
              ? t("clientHygiene.confirmFilterFallback", { count: applyConfirmCount })
              : t("clientHygiene.confirmApplyFallback", { count: applyConfirmCount })
          }
          irreversible
          changeLines={applyConfirmIsAutoStart ? [] : applyChangeLines}
          confirmCheckboxLabel={t("clientHygiene.confirmCheckbox")}
          reviewCheckboxLabel={
            applyConfirmIsAutoStart
              ? t("clientHygiene.reviewCheckbox")
              : t("clientHygiene.reviewApplyCheckbox")
          }
          onBack={() => setApplyConfirmOpen(false)}
          onConfirm={handleConfirmApply}
          confirmButtonLabel={
            applyConfirmIsAutoStart
              ? t("clientHygiene.startScan")
              : t("clientHygiene.applySelected", { count: applyConfirmCount })
          }
        />
      </Dialog>
    </>
  );
}
